import React from "react";
import SeoMeta from "../components/SeoMeta";
import { SiteNav } from "../components/home/SiteNav";
import { SiteFooter, Breathwork } from "../components/home/BreathworkAndFooter";
import { Reveal, Eyebrow } from "../components/home/shared";
import { PageHero } from "../components/site/Sections";
import { teamPage } from "../mock";

const TOM = [
  "After two decades as a property developer, years of building a business, searching and not being able to name what was missing, Tom reached his lowest point. What followed was therapy, deep inner work and a rebuilding that changed everything. Not just his life, but his entire lens on what was possible.",
  "From that process came the FERAL Method. He lived it before he built it.",
  "And with it came a dream: to own spaces in wild nature where people could come back to themselves. With little to no funds of his own, Tom set out to make it happen. He raised the investment, took the risks and worked relentlessly, often under real pressure, to bring three historic Anglesey estates into the FERAL family.",
  "Today, his greatest pride is seeing people walk through the doors. Some come for a retreat. Some come for a session with Calum, our in-house therapeutic practitioner, and leave having had a breakthrough. Some break bread with strangers at our long communal tables, sharing local, seasonal food from Aimee, our FERAL culinary curator. And some simply come to spend a few quiet days in one of our cottages.",
];

const ESTATES = [
  { name: "Bron Menai", copy: "Bron Menai is a country manor set in eight acres near Newborough, now fully renovated as a home for retreats, rest and reset.", image: "/bron-exterior.jpeg" },
  { name: "Plas Llechylched", copy: "Plas Llechylched is a historic courtyard estate built in 1866, reimagined for slow, communal living, moments from the wild beaches of Rhosneigr.", image: "/plas-l-exterior.jpeg" },
  { name: "Hotel Cymyran", copy: "Hotel Cymyran is the next chapter, and the future flagship home of FERAL.", image: "/cymyran-exterior.png" },
];

const Member = ({ m }) => (
  <Reveal>
    <figure data-testid={`story-member-${m.name.toLowerCase()}`}>
      <div className="overflow-hidden aspect-[3/4] bg-[#2c6052]">
        {m.image ? <img src={m.image} alt={m.name} style={{ objectPosition: m.position || "center 15%" }} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
          : <div className="w-full h-full flex items-center justify-center"><img src="/feral-logo-yellow.png" alt="" className="w-20 opacity-80" /></div>}
      </div>
      <figcaption className="pt-5">
        <p className="feral-serif text-[#9b3d0a] text-2xl">{m.name}</p>
        <p className="feral-header text-[11px] font-bold tracking-[0.2em] text-[#231f20]/60 mt-1">{m.role}</p>
        <p className="mt-3 text-[#231f20]/75 font-light leading-relaxed text-sm">{m.bio}</p>
      </figcaption>
    </figure>
  </Reveal>
);

export default function OurStory() {
  return (
    <div className="bg-[#f2e5de]" data-testid="our-story-page">
      <SeoMeta title="Our story | FERAL Collective" description="Time in nature. Space to breathe. A community to belong to. The story of FERAL, its founder Tom and three historic Anglesey estates." canonicalPath="/our-story" image="/home/tom.webp" />
      <SiteNav />
      <PageHero testid="story-hero" title="Our story." image="/home/anglesey-sea.webp"
        copy="Time in nature. Space to breathe. A community to belong to. Everything you need to come home to yourself. Be free, be FERAL." />

      <section className="feral-canvas py-24 md:py-36 px-6 md:px-10" data-testid="story-tom">
        <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5 md:sticky md:top-28 self-start">
            <img src="/home/tom.webp" alt="Tom, FERAL founder" className="w-full aspect-[4/5] object-cover" />
          </Reveal>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal><Eyebrow className="text-[#9b3d0a] mb-6">Tom's story</Eyebrow></Reveal>
            <Reveal delay={0.05}><p className="text-[#231f20]/85 text-lg md:text-xl font-light leading-relaxed" data-testid="story-tom-intro">{TOM[0]}</p></Reveal>
            <Reveal delay={0.1}><p className="feral-serif text-[#9b3d0a] text-3xl md:text-4xl leading-tight my-10">{TOM[1]}</p></Reveal>
            {TOM.slice(2).map((p, i) => <Reveal key={i} delay={0.05}><p className="text-lg font-light leading-relaxed text-[#231f20]/80 mb-6">{p}</p></Reveal>)}
            <Reveal><p className="feral-serif text-[#9b3d0a] text-2xl md:text-3xl leading-snug mt-10">Tom is building the place he wishes had existed when he needed it most.</p></Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-[#2c6052] text-[#f2e5de] py-28 md:py-40 px-6 md:px-10 overflow-hidden" data-testid="story-anglesey">
        <img src="/home/shore.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal><Eyebrow className="text-[#f2e5de]/80 mb-6">Why Anglesey</Eyebrow></Reveal>
          <Reveal delay={0.1}><p className="feral-serif text-[#ebc754] text-3xl md:text-5xl leading-tight">Anglesey has a pull that's hard to explain until you've felt it.</p></Reveal>
          <Reveal delay={0.2}><p className="mt-8 text-lg md:text-xl font-light leading-relaxed text-[#f2e5de]/85">Wild coastline, big skies, ancient woodland and the mountains of Eryri across the Menai Strait. It's a place that slows you down without asking. That's why FERAL is here, and why every estate is rooted in this landscape.</p></Reveal>
        </div>
      </section>

      <section className="feral-canvas py-24 md:py-36 px-6 md:px-10" data-testid="story-estates">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <Reveal className="md:col-span-5"><Eyebrow className="text-[#9b3d0a] mb-5">The estates</Eyebrow><h2 className="feral-serif text-[#9b3d0a] text-5xl sm:text-6xl leading-[0.95] tracking-tight">Coming home.</h2></Reveal>
            <Reveal className="md:col-span-7 space-y-5 text-lg font-light leading-relaxed text-[#231f20]/80" delay={0.1}>
              <p>Tom grew up in North Wales. After years of living and working around the world, he felt a pull to return to his homeland. When he did, he found that Anglesey was the perfect setting for what he wanted to create: wild coastline, big skies, ancient landscapes and a pace of life that lets people breathe again.</p>
              <p>He lived on the island for a number of years before FERAL found its first home. The estates weren't so much searched for as discovered, each one arriving at the right moment, almost as if it had been waiting. Tom has always felt he was meant to be their custodian, and to give each one a new life as a place where FERAL could exist.</p>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {ESTATES.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.1}>
                <div data-testid={`story-estate-${i}`}>
                  <div className="overflow-hidden aspect-[4/5]"><img src={e.image} alt={e.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" /></div>
                  <h3 className="feral-serif text-[#9b3d0a] text-3xl mt-6">{e.name}</h3>
                  <p className="mt-3 text-[#231f20]/75 font-light leading-relaxed">{e.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="collective" className="bg-[#f2e5de] py-24 md:py-36 px-6 md:px-10 scroll-mt-20" data-testid="story-collective">
        <div className="max-w-[1500px] mx-auto">
          <Reveal><h2 className="feral-serif text-[#9b3d0a] text-5xl sm:text-6xl leading-[0.95] tracking-tight mb-16">The collective.</h2></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-14">
            {teamPage.members.map((m) => <Member key={m.name} m={m} />)}
          </div>
        </div>
      </section>

      <Breathwork id="join-us" source="our-story" copy="Get a free 20-minute meditation session with Calum, straight to your inbox." />
      <SiteFooter />
    </div>
  );
}
